import { useEffect, useRef } from 'react';

export default function EnvironmentalManagement() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const managementItems = [
    {
      icon: 'ri-file-list-3-line',
      title: '環境マネジメントシステム',
      description: 'ISO14001に準拠した環境マネジメントシステムを構築し、継続的な環境改善活動を推進しています。',
      color: 'emerald'
    },
    {
      icon: 'ri-line-chart-line',
      title: '環境目標の設定と達成',
      description: '明確な環境目標を設定し、定期的なモニタリングと評価により、確実な目標達成を目指しています。',
      color: 'emerald'
    },
    {
      icon: 'ri-team-line',
      title: '従業員教育',
      description: '全従業員に対する環境教育を実施し、環境意識の向上と実践的な環境保全活動を促進しています。',
      color: 'emerald'
    },
    {
      icon: 'ri-file-text-line',
      title: '環境報告書の公開',
      description: '年次環境報告書を作成し、ステークホルダーに対して透明性の高い情報開示を行っています。',
      color: 'emerald'
    }
  ];

  const sdgs = [
    { icon: 'ri-sun-line', title: 'エネルギーをみんなに そしてクリーンに' },
    { icon: 'ri-tools-line', title: '産業と技術革新の基盤をつくろう' },
    { icon: 'ri-building-2-line', title: '住み続けられるまちづくりを' },
    { icon: 'ri-recycle-line', title: 'つくる責任 つかう責任' },
    { icon: 'ri-cloud-windy-line', title: '気候変動に具体的な対策を' },
    { icon: 'ri-drop-line', title: '海の豊かさを守ろう' },
    { icon: 'ri-plant-line', title: '陸の豊かさも守ろう' }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
            <span className="text-sm font-semibold text-emerald-700 tracking-wide">ENVIRONMENTAL MANAGEMENT</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">環境配慮型の企業運営</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            環境マネジメントシステムに基づいた体系的な環境保全活動により、<br />持続可能な企業運営を実現しています。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {managementItems.map((item, index) => (
            <div
              key={index}
              className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000 bg-gradient-to-br from-white to-emerald-50/50 rounded-2xl p-8 border border-emerald-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-12 text-white">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold mb-4">SDGsへの貢献</h3>
            <p className="text-white/90 text-lg max-w-3xl mx-auto leading-relaxed">
              国連の持続可能な開発目標（SDGs）の達成に向けて、<br />私たちの事業活動を通じて積極的に貢献しています。
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {sdgs.map((sdg, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className={`${sdg.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-xs text-white/80 leading-tight">{sdg.title}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-award-line text-3xl text-emerald-600"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">環境認証取得</h4>
            <p className="text-gray-600 leading-relaxed">国際的な環境基準に準拠した認証を取得</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-3xl text-emerald-600"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">コンプライアンス遵守</h4>
            <p className="text-gray-600 leading-relaxed">環境関連法規制を厳格に遵守</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-global-line text-3xl text-emerald-600"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">国際連携</h4>
            <p className="text-gray-600 leading-relaxed">グローバルな環境保全活動に参画</p>
          </div>
        </div>
      </div>
    </section>
  );
}